import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const loadUser = useCallback(async () => {
    try {
      const response = await authAPI.getProfile();
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Failed to load user:', error);
      // Only logout on explicit 401 unauthorized errors
      // Don't logout on network errors or other temporary issues
      if (error.response?.status === 401) {
        logout();
      } else {
        // For other errors (network, 500, etc), just set loading false
        // but keep any existing auth state - token might still be valid
        console.warn('Error loading user, but not logging out:', error.message);
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      loadUser();
    } else {
      setLoading(false);
    }
  }, [token, loadUser]);

  const login = async (email, password, authenticatorCode = null, emailCode = null) => {
    try {
      const response = await authAPI.login({ 
        email, 
        password,
        authenticator_code: authenticatorCode,
        email_code: emailCode
      });
      
      const { access_token, requires_2fa, two_factor_method, temp_token } = response.data;
      
      // Check if 2FA is required
      if (requires_2fa) {
        return { 
          success: false, 
          requires_2fa: true,
          two_factor_method,
          temp_token,
          email // Pass email for 2FA verification step
        };
      }
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      
      try {
        const userResponse = await authAPI.getProfile();
        setUser(userResponse.data);
        setIsAuthenticated(true);
        return { success: true, user: userResponse.data };
      } catch (error) {
        console.error('Failed to load user after login:', error);
        return { success: true, user: null };
      }
    } catch (error) {
      console.error('Login failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const loginWithTelegram = async (telegramData) => {
    try {
      const response = await authAPI.telegramLogin(telegramData);
      const { access_token } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      
      try {
        const userResponse = await authAPI.getProfile();
        setUser(userResponse.data);
        setIsAuthenticated(true);
        return { success: true, user: userResponse.data };
      } catch (error) {
        console.error('Failed to load user after Telegram login:', error);
        return { success: true, user: null };
      }
    } catch (error) {
      console.error('Telegram login failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Telegram login failed' 
      };
    }
  };

  const loginWithGoogle = async (sessionId) => {
    try {
      const response = await authAPI.googleSession(sessionId);
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      setIsAuthenticated(true);
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Google login failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Google login failed' 
      };
    }
  };

  const register = async (email, password, role = 'user') => {
    try {
      const response = await authAPI.register({ 
        email, 
        password,
        role,
        is_active: true,
        language: 'ru'
      });
      
      // Auto login after registration
      return await login(email, password);
    } catch (error) {
      console.error('Registration failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    login,
    loginWithTelegram,
    loginWithGoogle,
    register,
    logout,
    updateUser,
    loadUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
